/**
 * Region Management Manager
 * Handles region-specific operations and state management
 */

import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {TranslateService} from '@ngx-translate/core';
import {RegionService} from '@6grain/masofa-monolith-api-client/dictionaries/api/region.service';
import {Region, RegionMap, RegionMapService} from '@6grain/masofa-monolith-api-client/dictionaries';
import {GisPolygon} from '../interfaces/gis-component.interfaces';
import {forkJoin, lastValueFrom, map, Observable,} from 'rxjs';
import {ServerTranslateService} from '@services/server-translate.service';
import {extractCoordinatesFromWKT, extractCoordinatesFromWKTFlat} from '@app/utils/wkt-parser';
import {PolygonConverterUtil} from '@app/utils/polygon-converter.util';
import {IndexedDBCacheService} from '@app/services/indexeddb-cache.service';
import {AuthService} from '@services/auth.service';
import {RegionDictionaryNode} from '@app/services/region-dictionary.service';
import {MapLayersController} from '@components/openstreetmap/controllers';
import {environment} from '@root/src/environments/environment';
import {replaceFilter} from '@app/utils/_msf';
import {FilterOperator} from '@app/utils/filter-operator';

/** Ответ бэкенда: дата последнего обновления RegionMap (PascalCase или camelCase) */
export interface RegionMapLastUpdateResponse {
  lastUpdateAt?: string;
  LastUpdateAt?: string;
}

@Injectable({providedIn: 'root'})
export class RegionManagerService {
  private translate = inject(TranslateService);
  private regionService = inject(RegionService);
  private regionMapService = inject(RegionMapService);
  private http = inject(HttpClient);
  private serverTranslate = inject(ServerTranslateService);
  private cacheService = inject(IndexedDBCacheService);
  private authService = inject(AuthService);
  private currentLayersController: any = null;

  // Cache keys
  private readonly REGIONS_CACHE_KEY = 'regions_data_cache';
  private readonly REGION_MAPS_CACHE_KEY = 'region_maps_cache';
  private readonly REGION_MAPS_LAST_UPDATE_KEY = 'region_maps_last_update_at';

  private SELECTED_REGION_COLOR = '#F57C00';
  private REGION_BORDER_COLOR = '#0000ff';

  // Flag to track if background update is in progress
  private isBackgroundUpdateInProgress = false;

  // Cache for region polygons with their centers
  private regionPolygonsCache: Map<string, { polygon: GisPolygon, center: [number, number] }> = new Map();

  // Cache for region IDs
  private regionIdsCache: string[] = [];

  /** In-memory region id → administrative `level` (from dictionaries), for GIS filters. */
  private readonly regionLevelById = new Map<string, number>();

  // Hover state properties
  hoveredRegion: Region | null = null;
  lastHoveredRegion: Region | null = null;
  hoveredDistrict: Region | null = null;
  lastHoveredDistrict: Region | null = null;

  constructor() {
    // Subscribe to language changes to update region labels
    this.translate.onLangChange.subscribe(() => {
      if (this.currentLayersController) {
        this.updateRegionLabels(this.currentLayersController);
      }
    });

    // Subscribe to login success events to update cache
    this.authService.loginSuccess$.subscribe(() => {
      this.updateCacheAfterLogin().catch(err => {
        console.error('Background cache update failed:', err);
      });
    });
  }

  /**
   * Update cache after successful login
   * Fetches fresh data from server and updates cache if size changed
   */
  async updateCacheAfterLogin(): Promise<void> {
    if (this.isBackgroundUpdateInProgress) {
      return;
    }

    this.isBackgroundUpdateInProgress = true;

    try {
      // Fetch fresh data from server
      const [regions, regionMaps] = await Promise.all([
        lastValueFrom(this.regionService.dictionariesRegionGetByQueryPost({})),
        lastValueFrom(this.regionMapService.dictionariesRegionMapGetByQueryPost({}))
      ]);

      this.syncRegionLevelIndex(regions);

      const regionsJson = JSON.stringify(regions);
      const regionMapsJson = JSON.stringify(regionMaps);

      // Check if data size changed
      const [regionsChanged, regionMapsChanged] = await Promise.all([
        this.cacheService.hasDataSizeChanged(this.REGIONS_CACHE_KEY, regionsJson),
        this.cacheService.hasDataSizeChanged(this.REGION_MAPS_CACHE_KEY, regionMapsJson)
      ]);

      if (regionsChanged) {
        await this.cacheService.set(this.REGIONS_CACHE_KEY, regionsJson);
      }

      if (regionMapsChanged) {
        await this.cacheService.set(this.REGION_MAPS_CACHE_KEY, regionMapsJson);
        const lastUpdateAt = await this.getRegionMapLastUpdateAtFromServer();
        if (lastUpdateAt) {
          await this.cacheService.set(this.REGION_MAPS_LAST_UPDATE_KEY, lastUpdateAt);
        }
      }

    } catch (error) {
      console.error('Failed to update cache after login:', error);
    } finally {
      this.isBackgroundUpdateInProgress = false;
    }
  }

  /**
   * Administrative level for a region id (0 = country, 1 = province, …), or null if unknown.
   * Populated when regions are loaded via {@link getRegions} / internal cache refresh.
   */
  getRegionLevelById(regionId: string | null | undefined): number | null {
    if (regionId == null || regionId === '') {
      return null;
    }
    const key = String(regionId);
    if (!this.regionLevelById.has(key)) {
      return null;
    }
    return this.regionLevelById.get(key)!;
  }

  private syncRegionLevelIndex(regions: Region[] | null | undefined): void {
    if (!Array.isArray(regions)) {
      return;
    }
    for (const r of regions) {
      const id = r?.id != null ? String(r.id) : '';
      const level = (r as { level?: number }).level;
      if (id && typeof level === 'number' && !Number.isNaN(level)) {
        this.regionLevelById.set(id, level);
      }
    }
  }

  /**
   * Get regions - always returns cached data first if available
   * Public method for accessing regions with cache
   */
  async getRegions(id?: string): Promise<Region[]> {
    return this.getRegionsAsync(id);
  }

  private async collectRegionAndChildrenIds(regionId: string): Promise<string[]> {
    const all = await this.getRegions();
    const targetId = String(regionId);

    const childrenByParent = new Map<string, string[]>();
    for (const r of all as any[]) {
      const id = r?.id != null ? String(r.id) : null;
      const parentId = r?.parentId != null ? String(r.parentId) : null;
      if (!id || !parentId) continue;
      const list = childrenByParent.get(parentId) ?? [];
      list.push(id);
      childrenByParent.set(parentId, list);
    }

    const result: string[] = [];
    const visited = new Set<string>();
    const stack: string[] = [targetId];

    while (stack.length) {
      const current = stack.pop()!;
      if (visited.has(current)) continue;
      visited.add(current);
      result.push(current);

      const children = childrenByParent.get(current);
      if (children?.length) {
        for (const childId of children) {
          stack.push(childId);
        }
      }
    }

    return result;
  }

  async includeChildInRequest(
    params: { regionId?: string | null | undefined },
    computedFilters: Array<{ filterField?: string | null; filterOperator?: number; filterValue?: any }>
  ): Promise<Array<{ filterField?: string | null; filterOperator?: number; filterValue?: any }>> {
    if (!params?.regionId) {
      return computedFilters;
    }

    const regionIds = await this.collectRegionAndChildrenIds(params.regionId);
    replaceFilter(
      'regionId',
      {
        filterField: 'regionId',
        filterOperator: FilterOperator.AnyOf,
        filterValue: regionIds,
      },
      computedFilters as any
    );

    return computedFilters;
  }

  async includeChildInFilters(
    computedFilters: Array<{ filterField?: string | null; filterOperator?: number; filterValue?: any }>
  ): Promise<Array<{ filterField?: string | null; filterOperator?: number; filterValue?: any }>> {
    const regionFilter = computedFilters.find(f => f?.filterField === 'regionId');
    if (!regionFilter) {
      return computedFilters;
    }

    const rawRegionId = regionFilter.filterValue;
    if (rawRegionId == null) {
      return computedFilters;
    }

    if (Array.isArray(rawRegionId)) {
      replaceFilter('regionId', {
        filterField: 'regionId',
        filterOperator: FilterOperator.AnyOf,
        filterValue: rawRegionId.map(v => String(v)),
      }, computedFilters as any);
      return computedFilters;
    }

    const regionIds = await this.collectRegionAndChildrenIds(String(rawRegionId));
    replaceFilter('regionId', {
      filterField: 'regionId',
      filterOperator: FilterOperator.AnyOf,
      filterValue: regionIds,
    }, computedFilters as any);

    return computedFilters;
  }

  getRegionsObservable(): Observable<Region[] | Region> {
    return new Observable(observer => {
      (async () => {
        try {
          const regions = await this.getRegionsAsync();
          observer.next(regions);
          observer.complete();
        } catch (error) {
          console.warn('Failed to fetch regions', error);
          observer.error(error);
        }
      })();
    });
  }

  private async getRegionsAsync(id?: string): Promise<Region[]> {

    // Try to get from cache first
    try {
      const cachedData = await this.cacheService.get(this.REGIONS_CACHE_KEY);
      if (cachedData) {
        try {
          const cachedRegions = JSON.parse(cachedData) as Region[];
          this.syncRegionLevelIndex(cachedRegions);
          if (id) {
            const region = cachedRegions.find(r => r.id === id);
            if (region) {
              return [region];
            }
          } else {
            return cachedRegions;
          }
        } catch (error) {
          console.warn('Failed to parse cached regions, fetching from API', error);
        }
      }
    } catch (e) {
      console.warn('Error reading cached regions', e);
    }

    // No cache available, fetch from API
    try {
      const regions = await lastValueFrom(this.regionService.dictionariesRegionGetByQueryPost({}));
      this.syncRegionLevelIndex(regions);
      try {
        await this.cacheService.set(this.REGIONS_CACHE_KEY, JSON.stringify(regions));
      } catch (err) {
        console.warn('Failed to cache regions', err);
      }
      if (id) {
        const region = regions.find(r => r.id === id);
        if (region) {
          return [region];
        }
      }
      return regions;
    } catch (apiErr) {
      console.warn('Failed to fetch regions from API', apiErr);
      return [];
    }
  }

  /**
   * Get region maps - always returns cached data first if available
   * Public method for accessing region maps with cache
   */
  async getRegionMaps(): Promise<RegionMap[]> {
    return this.getRegionMapsAsync();
  }

  /**
   * Запрос даты последнего обновления RegionMap с бэкенда (для валидации кеша).
   */
  private async getRegionMapLastUpdateAtFromServer(): Promise<string | null> {
    try {
      const url = `${environment.api}/dictionaries/RegionMap/GetLastUpdateAt`;
      const token = this.authService.getToken();
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        ...(token ? {Authorization: `Bearer ${token}`} : {})
      });
      const res = await lastValueFrom(
        this.http.get<RegionMapLastUpdateResponse>(url, {headers})
      );
      return res?.lastUpdateAt ?? res?.LastUpdateAt ?? null;
    } catch (e) {
      console.warn('Failed to get RegionMap last update date', e);
      return null;
    }
  }

  private async getRegionMapsAsync(): Promise<RegionMap[]> {
    // Сначала запрашиваем дату последней правки RegionMap с бэкенда
    const serverLastUpdateAt = await this.getRegionMapLastUpdateAtFromServer();

    // Пробуем кеш: если дата на фронте указана и не старее серверной — берём из кеша
    try {
      const cachedData = await this.cacheService.get(this.REGION_MAPS_CACHE_KEY);
      const cachedLastUpdateAt = await this.cacheService.get(this.REGION_MAPS_LAST_UPDATE_KEY);

      if (cachedData) {
        const hasValidDate = cachedLastUpdateAt && serverLastUpdateAt;
        const cacheIsActual = hasValidDate && cachedLastUpdateAt >= serverLastUpdateAt;

        if (cacheIsActual) {
          try {
            const regionMaps = JSON.parse(cachedData) as RegionMap[];
            console.log('Using cached region maps data (lastUpdateAt valid)');
            return regionMaps;
          } catch (error) {
            console.warn('Failed to parse cached region maps, fetching from API', error);
          }
        }
        // Дата не указана, или кеш старее серверной — запрашиваем новые данные
      }
    } catch (e) {
      console.warn('Error reading cached region maps', e);
    }

    // Запрашиваем новые данные с API
    try {
      const regionMaps = await lastValueFrom(this.regionMapService.dictionariesRegionMapGetByQueryPost({}));
      try {
        await this.cacheService.set(this.REGION_MAPS_CACHE_KEY, JSON.stringify(regionMaps));
        if (serverLastUpdateAt) {
          await this.cacheService.set(this.REGION_MAPS_LAST_UPDATE_KEY, serverLastUpdateAt);
        }
      } catch (err) {
        console.warn('Failed to cache region maps', err);
      }
      return regionMaps;
    } catch (apiErr) {
      console.warn('Failed to fetch region maps from API', apiErr);
      return [];
    }
  }

  /**
   * Get region tree (for dropdowns and filters)
   * Uses cache if available, builds tree from regions level 2 and 3
   */
  async getRegionTree(): Promise<RegionDictionaryNode[]> {
    const regions = await this.getRegions();

    // Filter regions by level 2 and 3
    const level2Regions = regions.filter(r => r.level === 2);
    const level3Regions = regions.filter(r => r.level === 3);

    // Build tree structure: level2 -> level3
    return level2Regions.map(region => {
      const children = level3Regions
        .filter(child => child.parentId === region.id)
        .map(child => {
          const childName = this.getRegionName(child);
          const childNames = this.convertLocalizationStringToRecord(child.names);
          return {
            id: String(child.id),
            name: childName,
            parentId: child.parentId ? String(child.parentId) : null,
            level: child.level,
            regionMapId: child.regionMapId ? String(child.regionMapId) : null,
            names: childNames,
            children: []
          } as RegionDictionaryNode;
        });

      const regionName = this.getRegionName(region);
      const regionNames = this.convertLocalizationStringToRecord(region.names);
      return {
        id: String(region.id),
        name: regionName,
        parentId: region.parentId ? String(region.parentId) : null,
        level: region.level,
        regionMapId: region.regionMapId ? String(region.regionMapId) : null,
        names: regionNames,
        children: children
      } as RegionDictionaryNode;
    });
  }

  /**
   * Get regions by map (for tile maps)
   * Uses cache if available
   */
  async getRegionsByMap(): Promise<Region[]> {
    return this.getRegions();
  }

  /**
   * Convert LocalizationString to Record<string, string>
   */
  private convertLocalizationStringToRecord(names?: any): Record<string, string> {
    if (!names) {
      return {};
    }
    // If it's already a Record, return it
    if (typeof names === 'object' && !Array.isArray(names)) {
      const result: Record<string, string> = {};
      for (const key in names) {
        if (names.hasOwnProperty(key) && typeof names[key] === 'string') {
          result[key] = names[key];
        }
      }
      return result;
    }
    return {};
  }

  toPolygon(filters?: Array<{
    filterField: string,
    filterValue: string,
    filterOperator: number
  }>): Observable<GisPolygon[]> {

    // Load both regions and region maps in parallel, using cache when available
    return forkJoin({
      regions: this.getRegions(),
      regionMaps: this.getRegionMaps()
    }).pipe(
      map(({regions, regionMaps}) => {
        return this.convertRegionsToPolygons(regions, regionMaps);
      })
    );
  }

  /**
   * Convert regions directly to polygons with zoom level visibility
   */
  private convertRegionsToPolygons(regions: Region[], regionMaps: RegionMap[]): GisPolygon[] {
    const polygons: GisPolygon[] = [];

    // Clear the cache before processing new regions
    this.regionPolygonsCache.clear();

    // Index region maps by regionMapId for quick lookup
    const regionMapIndex = new Map<string, RegionMap>();

    regionMaps.forEach(rm => {
      if (rm.id) {
        regionMapIndex.set(rm.id, rm);
      }
    });

    // Process each region directly
    regions.forEach((region, index) => {
      // Find the region map
      const regionMap = region.regionMapId ? regionMapIndex.get(region.regionMapId) : undefined;

      if (regionMap && regionMap?.polygonArrayJson) {


        const polygonJson: string = regionMap.polygonJson as string;

        // regionMap?.polygonArrayJson.forEach((polygonJson, index) => {

        try {
          // Extract coordinates from WKT or GeoJSON format
          const coordinates = extractCoordinatesFromWKTFlat(polygonJson);
          const wktCoordinates = extractCoordinatesFromWKT(polygonJson);

          if (coordinates && coordinates.length > 0) {
            // Calculate the region level
            const regionLevel = region.level as number

            // Calculate center of polygon for circle marker
            const center = PolygonConverterUtil.getPolygonCenter(coordinates);

            // Configure zoom ranges based on region level
            let minZoom = 0;
            let maxZoom = 19;
            let showByZoomLevel = true;

            if (regionLevel === 0) {
              // Level 0 (Root/Country) - visible at very low zoom (0-7)
              minZoom = 0;
              maxZoom = 7;
            } else if (regionLevel === 1) {
              // Level 1 (Provinces/States) - visible at low-medium zoom (7-10)
              minZoom = 0;
              maxZoom = 0;
            } else if (regionLevel === 2) {
              // Level 2 (Districts) - visible at medium zoom (10-13)
              minZoom = 0;
              // Keep one-step overlap with level 3 to avoid stale hover state at zoom boundaries.
              maxZoom = 10;
            } else if (regionLevel === 3) {
              // Level 3 (Sub-districts) - visible at high zoom (13-16)
              // Keep one-step overlap with level 2 to avoid stale hover state at zoom boundaries.
              minZoom = 9;
              maxZoom = 30;
            } else if (regionLevel >= 4) {
              // Level 4+ (Detailed regions) - visible at very high zoom (16+)
              minZoom = 16;
              maxZoom = 19;
            } else {
              // Unknown level - always visible
              showByZoomLevel = false;
            }

            // Create UNIQUE ID for each polygon part (for regions with multiple polygons like isolated cities)
            const uniquePolygonId = region.id!;

            const polygon: GisPolygon = {
              id: uniquePolygonId,
              name: this.getRegionName(region),
              coordinates: coordinates,
              wktCoordinates: wktCoordinates,
              type: 'region',
              fillColor: undefined,
              fill: false,
              strokeColor: this.getRegionColor(regionLevel),
              strokeWidth: 3,
              data: {
                region: region,
                level: regionLevel,
                parentId: region.parentId,
                polygonIndex: index  // Track which part of the multi-polygon this is
              },
              // Zoom level visibility configuration
              showByZoomLevel: showByZoomLevel,
              minZoom: minZoom,
              maxZoom: maxZoom,
              onHover: (polygon: GisPolygon) => {
                // Region hovered
              },
              onClick: (polygon: GisPolygon) => {
                // Region clicked
              }
            };

            polygons.push(polygon);

            // Cache polygon with its center for circle marker creation
            // Use the unique ID as key
            this.regionPolygonsCache.set(uniquePolygonId, {
              polygon: polygon,
              center: [center?.lat || 0, center?.lng || 0]
            });
          }
        } catch (error) {
          // Failed to parse polygon
        }
        // })
      }
    });

    return polygons;
  }


  private getRegionName(region: Region): string {
    const translatedName = this.serverTranslate.translate(region, 'names');
    return (translatedName as string) || 'Unknown Region';
  }

  private getRegionColor(level: number): string {
    // // Different colors for different hierarchy levels
    // const colors = [
    //   '#0000ff', // Level 1 - Green
    //   '#0000ff', // Level 1 - Green
    //   '#0000ff', // Level 1 - Green
    //   '#0000ff', // Level 1 - Green
    //   '#0000ff', // Level 1 - Green
    // ];

    return this.REGION_BORDER_COLOR; //   colors[Math.min(level, colors.length - 1)];
  }

  /**
   * Calculate font size based on zoom level
   * Scales label size dynamically with map zoom
   */
  calculateFontSizeByZoom(zoomLevel: number): number {
    // Base font size calculation based on zoom level
    if (zoomLevel <= 4) {
      return 10;      // Very zoomed out
    } else if (zoomLevel <= 6) {
      return 12;      // Zoomed out
    } else if (zoomLevel <= 8) {
      return 14;      // Medium zoom
    } else if (zoomLevel <= 10) {
      return 16;      // Zoomed in
    } else if (zoomLevel <= 12) {
      return 18;      // More zoomed in
    } else if (zoomLevel <= 14) {
      return 20;      // Very zoomed in
    } else {
      return 22;      // Extremely zoomed in
    }
  }


  filterByRegion(regionId: string | undefined, layersController: MapLayersController): void {
    // Store the layers controller reference for language updates
    this.currentLayersController = layersController;

    // Get all polygons from the map
    if (layersController) {
      const allPolygons = layersController.getAllPolygons();

      // Reset all region polygons to red color first
      allPolygons.forEach((polygonData: any) => {
        if (polygonData.properties?.type === 'region' && polygonData.polygon) {
          if (polygonData.polygon._forcedVisibleByRegionFilter) {
            polygonData.polygon._minZoom = polygonData.polygon._originalMinZoom;
            polygonData.polygon._maxZoom = polygonData.polygon._originalMaxZoom;
            polygonData.polygon._forcedVisibleByRegionFilter = false;
          }
          const regionLevel = polygonData.properties?.data?.level || 0;
          polygonData.polygon.setStyle({
            color: this.getRegionColor(regionLevel),
            weight: 3,
            fill: false,
          });
          polygonData.polygon.bringToBack();
        }
      });


      // Handle region filter with map centering
      if (regionId) {
        const matchingPolygon: {
          polygon: any,
          coordinates: any,
          properties: GisPolygon,
        } = allPolygons.find((polygonData: any) =>
          polygonData.properties?.data?.region?.id === regionId
        );

        if (matchingPolygon) {
          // Highlight the selected region with yellow border
          if (matchingPolygon.polygon) {
            if (matchingPolygon.polygon._showByZoomLevel) {
              matchingPolygon.polygon._originalMinZoom = matchingPolygon.polygon._minZoom;
              matchingPolygon.polygon._originalMaxZoom = matchingPolygon.polygon._maxZoom;
              matchingPolygon.polygon._minZoom = 0;
              matchingPolygon.polygon._maxZoom = 30;
              matchingPolygon.polygon._forcedVisibleByRegionFilter = true;
            }
            matchingPolygon.polygon.setStyle({
              color: this.SELECTED_REGION_COLOR,
              strokeWidth: 7,
              weight: 5,
            });
            matchingPolygon.polygon.bringToFront();
          }

          // Center map to the selected region
          if (matchingPolygon?.properties?.wktCoordinates) {
            layersController.centerToMultiPolygon(matchingPolygon?.properties?.wktCoordinates as [number, number][][][], true);
          }
          layersController.updateAllPolygonsVisibilityByZoom();
        }
      } else {
        layersController.updateAllPolygonsVisibilityByZoom();
      }
    }
  }

  removeHighlight(layersController: any): void {
    this.currentLayersController = layersController;

    // Get all polygons from the map
    if (layersController) {
      const allPolygons = layersController.getAllPolygons();
      allPolygons.forEach((polygonData: any) => {
        if (polygonData.properties?.type === 'region' && polygonData.polygon) {
          if (polygonData.polygon._forcedVisibleByRegionFilter) {
            polygonData.polygon._minZoom = polygonData.polygon._originalMinZoom;
            polygonData.polygon._maxZoom = polygonData.polygon._originalMaxZoom;
            polygonData.polygon._forcedVisibleByRegionFilter = false;
          }
          const regionLevel = polygonData.properties?.data?.level || 0;
          polygonData.polygon.setStyle({
            color: this.getRegionColor(regionLevel),
            weight: 3,
            fill: false,
          });
        }
      });
      layersController.updateAllPolygonsVisibilityByZoom();
    }
  }

  /**
   * Update region labels when language changes
   */
  private updateRegionLabels(layersController: any): void {
    if (!layersController) return;

    const allPolygons = layersController.getAllPolygons();

    allPolygons.forEach((polygonData: any) => {
      if (polygonData.properties?.type === 'region' && polygonData.properties?.data?.region) {
        const region = polygonData.properties.data.region;
        // Update the properties name
        polygonData.properties.name = this.getRegionName(region);

      }
    });
  }

  /**
   * Configure polygon events with hover and click handlers
   * This method is called after polygons are loaded to set up interactions
   */
  configurePolygonEvents(
    polygons: GisPolygon[],
    mapOptions: { showRegionNames?: boolean },
    onPolygonClick?: (lat: number, lng: number, polygonData: any) => void
  ): GisPolygon[] {

    polygons.forEach(polygon => {
      const regionLevel = polygon.data?.region?.level || 0;

      // Configure hover events based on region level
      if (regionLevel === 2) {
        // Level 2 regions: only set hoveredRegion
        polygon.onHover = (poly) => {
          if (mapOptions.showRegionNames) {
            this.hoveredRegion = poly?.data?.region;
            this.lastHoveredRegion = poly?.data?.region;
            // Reset district state when hovering a parent region
            // to avoid stale district labels in the modal header.
            this.hoveredDistrict = null;
            this.lastHoveredDistrict = null;
          }
        }
        polygon.onHoverOut = (poly) => {
          this.hoveredRegion = null;
          this.hoveredDistrict = null;
        }
      } else if (regionLevel === 3) {
        // Level 3 regions: set both hoveredDistrict and hoveredRegion
        polygon.onHover = (poly) => {
          if (mapOptions.showRegionNames) {
            this.hoveredDistrict = poly?.data?.region;
            this.lastHoveredDistrict = poly?.data?.region;
            // Also set parent region if available
            if (poly?.data?.parentId) {
              // Find parent region from the data
              const parentRegion = polygons.find(p => p.id === poly.data.parentId);
              if (parentRegion) {
                this.hoveredRegion = parentRegion.data?.region;
              }
            }
          }
        }
        polygon.onHoverOut = (poly) => {
          this.hoveredDistrict = null;
          this.hoveredRegion = null;
        }
      } else {
        // Other levels: default behavior (set hoveredRegion)
        polygon.onHover = (poly) => {
          if (mapOptions.showRegionNames) {
            this.hoveredRegion = poly?.data?.region;
          }
        }
        polygon.onHoverOut = (poly) => {
          this.hoveredRegion = null;
        }
      }

      // Configure click events
      polygon.onClick = (poly) => {
        if (poly.clickedLatLng?.length && onPolygonClick) {
          const lat = poly.clickedLatLng[0];
          const lng = poly.clickedLatLng[1];
          const polygonData = {
            id: poly.id,
            name: poly.data?.region?.names ?
              this.serverTranslate.translate(poly.data.region, 'names') :
              'Region Polygon',
            type: 'Polygon',
            polygonJson: [],
            region: poly.data?.region
          };

          onPolygonClick(lat, lng, polygonData);
        }
      }
    });

    return polygons;
  }

  /**
   * Get the current region path string showing the hierarchy
   * Example: "Kazakhstan / Region Name / District Name"
   */
  getRegionPathString(countryName: string = 'Lebanon', allRegionsLabel: string = 'ALL_REGIONS'): string {
    const parts: string[] = [countryName];

    if (this.hoveredRegion && this.hoveredRegion.names) {
      parts.push(this.serverTranslate.translate(this.hoveredRegion, 'names') as string);
    }

    if (this.hoveredDistrict && this.hoveredDistrict.names) {
      parts.push(this.serverTranslate.translate(this.hoveredDistrict, 'names') as string);
    }

    // If no region or district is hovered, add "All Regions"
    if (!this.hoveredRegion && !this.hoveredDistrict) {
      parts.push(allRegionsLabel);
    }

    return parts.join(' / ');
  }

  /**
   * Get the last hovered region path string
   * Example: "Region Name / District Name"
   */
  getLastRegionPath(): string {
    const parts: string[] = [];

    if (this.lastHoveredRegion && this.lastHoveredRegion.names) {
      parts.push(this.serverTranslate.translate(this.lastHoveredRegion, 'names') as string);
    }

    const districtHasMatchingParent =
      !!this.lastHoveredDistrict?.parentId &&
      !!this.lastHoveredRegion?.id &&
      String(this.lastHoveredDistrict.parentId) === String(this.lastHoveredRegion.id);
    const shouldShowDistrict =
      !!this.lastHoveredDistrict?.names &&
      (!this.lastHoveredRegion || districtHasMatchingParent);

    if (shouldShowDistrict) {
      parts.push(this.serverTranslate.translate(this.lastHoveredDistrict, 'names') as string);
    }

    return parts.join(' / ');
  }

  /**
   * Get the cached region polygons with their centers
   * Useful for creating circle markers or accessing polygon data
   */
  getRegionPolygonsCache(): Map<string, { polygon: GisPolygon, center: [number, number] }> {
    return this.regionPolygonsCache;
  }

  /**
   * Get a specific region polygon from cache by ID
   */
  getCachedRegionPolygon(regionId: string): { polygon: GisPolygon, center: [number, number] } | undefined {
    return this.regionPolygonsCache.get(regionId);
  }

  /**
   * Cache region IDs from the polygon cache
   * This method extracts and stores region IDs for efficient retrieval
   */
  cacheRegionIds(): void {
    const ids: string[] = [];

    // Extract region IDs from polygon cache
    this.regionPolygonsCache.forEach((value, key) => {
      // Only add base region IDs (not parts like "id_part_")
      if (!key.includes('_part_')) {
        ids.push(key);
      } else {
        // For multi-part polygons, extract the base ID
        const baseId = key.split('_part_')[0];
        if (!ids.includes(baseId)) {
          ids.push(baseId);
        }
      }
    });

    this.regionIdsCache = ids;
  }

  /**
   * Get cached region IDs
   * Returns an array of region IDs from the cache
   */
  getCachedRegionIds(): string[] {
    // If cache is empty, populate it from polygon cache
    if (this.regionIdsCache.length === 0) {
      this.cacheRegionIds();
    }

    return this.regionIdsCache;
  }

  /**
   * Get region hierarchy path by region ID
   * Constructs the full hierarchy path for a given region ID based on the selected language
   * Example: "Parent Region / Region / District / Sub-District"
   *
   * @param regionId - The ID of the region to get the hierarchy for
   * @returns Promise<string> - The full hierarchy path separated by ' / '
   *
   * Logic:
   * - Follows the parent chain from the given region up to the root
   * - Stops when parentId is null, undefined, or equal to the region's own ID (circular reference)
   * - Uses the current language for translations
   */
  async getRegionPathStringById(regionId: string): Promise<string> {
    try {
      // Get all regions from cache or API
      const regions = await this.getRegions();

      // Build a map for quick region lookup by ID
      const regionMap = new Map<string, Region>();
      regions.forEach(region => {
        if (region.id) {
          regionMap.set(region.id, region);
        }
      });

      // Build the hierarchy path by following parent chain
      const pathParts: string[] = [];
      let currentId: string | undefined = regionId;
      const visitedIds = new Set<string>(); // To detect circular references

      while (currentId) {
        // Prevent infinite loops from circular references
        if (visitedIds.has(currentId)) {
          break;
        }

        visitedIds.add(currentId);

        // Get the current region
        const region = regionMap.get(currentId);

        if (!region) {
          break;
        }

        // Add region name to the path (at the beginning to build hierarchy from parent to child)
        const regionName = this.getRegionName(region);
        pathParts.unshift(regionName);

        // Check if we should continue to parent
        const parentId = region.parentId;

        // Stop if parentId is null, undefined, or equal to current ID (root reached or circular reference)
        if (!parentId || parentId === currentId) {
          break;
        }

        // Move to parent
        currentId = parentId;
      }

      // Join all parts with ' / ' separator
      return pathParts.join(' / ');

    } catch (error) {
      console.error('Failed to get region path by ID:', error);
      return '';
    }
  }

  /**
   * Clear region cache persisted in IndexedDB/localStorage and in-memory state.
   * Intended for logout to avoid reusing stale user-scoped region data.
   */
  async clearRegionCacheOnLogout(): Promise<void> {
    try {
      await Promise.all([
        this.cacheService.clear(this.REGIONS_CACHE_KEY),
        this.cacheService.clear(this.REGION_MAPS_CACHE_KEY),
        this.cacheService.clear(this.REGION_MAPS_LAST_UPDATE_KEY),
      ]);
    } catch (error) {
      console.warn('Failed to clear region cache from indexed storage:', error);
    }

    // Always clear runtime caches/state as well.
    this.regionPolygonsCache.clear();
    this.regionIdsCache = [];
    this.hoveredRegion = null;
    this.lastHoveredRegion = null;
    this.hoveredDistrict = null;
    this.lastHoveredDistrict = null;
  }



}
